import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { PlayerProvider } from "@/context/PlayerContext";
import { BrandProvider } from "@/context/BrandContext";
import AppLayout from "@/components/AppLayout";
import SplashScreen from "@/components/SplashScreen";
import Browse from "@/pages/Browse";
import Favorites from "@/pages/Favorites";
import Schedule from "@/pages/Schedule";
import Requests from "@/pages/Requests";
import Chat from "@/pages/Chat";
import Admin from "@/pages/Admin";
import Advertise from "@/pages/Advertise";
import MySpots from "@/pages/MySpots";
import PaymentSuccess from "@/pages/PaymentSuccess";
import LiveShow from "@/pages/LiveShow";
import EpisodePage from "@/pages/EpisodePage";
import HostStudio from "@/pages/HostStudio";
import Profile from "@/pages/Profile";
import HostProfile from "@/pages/HostProfile";
import PrayerRequests from "@/pages/PrayerRequests";
import PrayerWall from "@/pages/PrayerWall";
import Bible from "@/pages/Bible";
import DevotionArchive from "@/pages/DevotionArchive";
import HostBio from "@/pages/HostBio";
import Events from "@/pages/Events";
import MyEvents from "@/pages/MyEvents";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Legal from "@/pages/Legal";
import ResetPassword from "@/pages/ResetPassword";
import ForgotAccount from "@/pages/ForgotAccount";

function ProtectedRoute({ children, adminOnly }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A1030] text-white">
        <div className="w-8 h-8 border-2 border-[#E6B34A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function ImpersonationBanner() {
  const { impersonating, user, stopImpersonating } = useAuth();
  if (!impersonating || !user) return null;
  const stop = async () => { await stopImpersonating(); window.location.assign("/admin"); };
  return (
    <div data-testid="impersonation-banner" className="fixed top-0 inset-x-0 z-[9999] bg-[#E6B34A] text-[#0A1030] px-4 py-2 flex items-center justify-center gap-3 text-sm font-semibold shadow-lg">
      <span>Viewing as <b>{user.name || user.email}</b></span>
      <button onClick={stop} data-testid="stop-impersonating" className="px-3 py-1 rounded-full bg-[#0A1030] text-white text-xs font-bold hover:bg-[#141C42] transition-colors">Return to admin</button>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <SplashScreen />
      <AuthProvider>
        <BrandProvider>
        <PlayerProvider>
          <BrowserRouter>
            <Toaster theme="dark" position="top-center" richColors />
            <ImpersonationBanner />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/forgot-account" element={<ForgotAccount />} />
              <Route element={<AppLayout />}>
                <Route path="/" element={<Browse />} />
                <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
                <Route path="/schedule" element={<Schedule />} />
                <Route path="/requests" element={<ProtectedRoute><Requests /></ProtectedRoute>} />
                <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                <Route path="/advertise" element={<Advertise />} />
                <Route path="/live" element={<LiveShow />} />
                <Route path="/episodes/:id" element={<EpisodePage />} />
                <Route path="/host" element={<ProtectedRoute><HostStudio /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/hosts/:id" element={<HostProfile />} />
                <Route path="/prayer" element={<ProtectedRoute><PrayerRequests /></ProtectedRoute>} />
                <Route path="/prayer-wall" element={<PrayerWall />} />
                <Route path="/bible" element={<Bible />} />
                <Route path="/devotions" element={<DevotionArchive />} />
                <Route path="/terms" element={<Legal which="terms" />} />
                <Route path="/privacy" element={<Legal which="privacy" />} />
                <Route path="/events" element={<Events />} />
                <Route path="/my-events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
                <Route path="/meet-the-host" element={<HostBio />} />
                <Route path="/my-spots" element={<ProtectedRoute><MySpots /></ProtectedRoute>} />
                <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </PlayerProvider>
        </BrandProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
